import { Component, ChangeDetectionStrategy, signal } from "@angular/core";
import { TranslateService } from '@ngx-translate/core';
import type { MenuItem } from 'primeng/api';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { MediaControlService } from '@medsurf/flat-services';
import { take } from "rxjs";

@Component({
    selector: 'medsurf-chapter-main-generate',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './generate.component.html',
    styleUrls: ['./generate.component.scss'],
    standalone: false
})
export class GenerateComponent {
  isGenerating = signal(false);
	
  pdfMenuItems: MenuItem[] = [
    {
      label: this.translate.instant('global.pdf_for_viewer'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PDF,
      command: () => this.#generatePdf(false)
    },
    {
      label: this.translate.instant('global.pdf_for_viewer_force'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PDF,
      command: () => this.#generatePdf(true)
    },
    {
      separator: true
    },
    {
      label: this.translate.instant('global.pdf_for_author'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PDF,
      command: () => this.#generateAuthorPdf(false)
    },
    {
      label: this.translate.instant('global.pdf_for_author_force'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PDF,
      command: () => this.#generateAuthorPdf(true)
    }
  ]  
  readonly FlatModels = FlatModels;
  
  constructor(public nodeSharedFacade: FlatFacades.NodeSharedFacade,
              public historySharedFacade: FlatFacades.HistorySharedFacade,
              public mediaControlService: MediaControlService,
              private translate: TranslateService,
              private alertControlFacade: FlatFacades.AlertControlFacade) {
  }

  downloadPdf(media: FlatModels.MediaEntityModels.MediaEntityType, type: 'author'| 'viewer') {
    if (!media) return;
    const url = this.mediaControlService.getPDFDownloadUrl(media, type);
    window.open(url, '_blank');
  }

  #generatePdf(forceGeneration: boolean) {
    if (this.isGenerating()) {
      return;
    }
    const node = this.nodeSharedFacade.snapshot_currentSelectedBlockNode();
    if (!node) return;
    this.isGenerating.set(true);
    this.nodeSharedFacade.requestGenerateChapterPdf(node.id, forceGeneration, false).pipe(take(1)).subscribe({
      error: (result) => {
        this.alertControlFacade.requestAddError(result, 'notification.chapter_pdf_failed');
        this.isGenerating.set(false);
      },
      next: () => {
        this.alertControlFacade.requestAddAlert({
          key: 'main',
          detail: 'notification.chapter_pdf_generated',
          severity: FlatModels.AlertControlModels.AlertType.enum.success,
          life: FlatModels.AlertControlModels.AlertTime.DEFAULT,
        });
        this.isGenerating.set(false);
      },
    });
  }

  #generateAuthorPdf(forceGeneration: boolean) {
    if (this.isGenerating()) {
      return;
    }
    const node = this.nodeSharedFacade.snapshot_currentSelectedBlockNode();
    if (!node) return;
    this.isGenerating.set(true);
    this.nodeSharedFacade.requestGenerateChapterPdf(node.id, forceGeneration, true).pipe(take(1)).subscribe({
      error: (result) => {
        this.alertControlFacade.requestAddError(result, 'notification.chapter_pdf_failed');
        this.isGenerating.set(false);
      },
      next: () => {
        this.alertControlFacade.requestAddAlert({
          key: 'main',
          detail: 'notification.chapter_pdf_generated',
          severity: FlatModels.AlertControlModels.AlertType.enum.success,
          life: FlatModels.AlertControlModels.AlertTime.DEFAULT,
        });
        this.isGenerating.set(false);
      },
    });
  }
}
